// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

import type { JobRunsResponse } from '@/admin-design/_common/jobs/types'
import { loginUrlFor } from '@/lib/loginRedirect'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = loginUrlFor(location.pathname + location.search)
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(
      res.status,
      (body as { error?: string } | null)?.error ?? res.statusText,
      body,
    )
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  commit: string
  built_at: string
}

// ─── API ──────────────────────────────────────────────────────────────────

// ─── Viral domain types ───────────────────────────────────────────────────

export type CampaignMode = 'trend' | 'brand' | 'repurpose' | 'topic' | 'manual' | 'affiliate'
export type CampaignStatus = 'active' | 'paused'

export interface ChannelRules {
  banned_phrases?: string[]
  cooldown_hours?: number
}

export interface PlatformConfig {
  enabled: boolean
  account?: string
  subreddits?: string[]
  flair?: string
  target_id?: number
  /** Minimum hours between any two posts from this account. */
  min_gap_hours?: number
  /** Minimum hours between two posts in the same channel. */
  channel_cooldown_hours?: number
  /** A channel's house rules, keyed by channel name (subreddit). */
  channel_rules?: Record<string, ChannelRules>
}

/** One move in a re-slot plan: a scheduled draft from its slot to a new one. */
export interface ReslotItem {
  draft_id: number
  from: string
  to: string
  /** The draft's first line, so the table can name it. */
  label?: string
}

export interface ReslotSkip {
  draft_id: number
  reason: string
}

export interface ReslotPlan {
  plan: ReslotItem[]
  skipped: ReslotSkip[]
  /** Posts whose slot the new rules would leave where it is. */
  unchanged: number
}

export interface ReslotResult {
  ok: boolean
  applied: number
  skipped: ReslotSkip[]
}

/** An unpublished draft or desk post that still uses a media file. */
export interface MediaUse {
  kind: 'draft' | 'desk_post'
  id: number
  status: string
  label: string
  where: string
}

export interface CampaignPacing {
  /** 'manual' stops generation while leaving the campaign active, so its posts
   *  keep being polled for engagement. */
  mode?: CampaignMode
  posts_per_day?: number
  posting_window_start?: string
  posting_window_end?: string
  timezone?: string
  platform_configs?: Record<string, PlatformConfig>
  /** Every mode but affiliate: approve drafts waiting in review without a
   *  person, oldest first. Affiliate campaigns use Affiliate.auto_approve. */
  auto_approve?: boolean
}

/** mode=affiliate: what a Coupang campaign writes about and how. */
export interface AffiliateConfig {
  /** coupang_categories ids. */
  category_ids: number[]
  /** Categories with no default account are open to this campaign too. */
  include_unassigned: boolean
  tone: 'promo' | 'review'
  /** Persona catalog key; empty = the account's voice. */
  persona?: string
  /** 0..100 on the normalized signal scale; 0 = take the best available. */
  min_signal_score: number
  /** 0 = the coupang.stale_age_hours setting. */
  stale_age_hours: number
  attach_image: boolean
  auto_approve: boolean
  /** Telegram notice 30 minutes before each post. Missing = on. */
  heads_up?: boolean
}

export interface Campaign {
  ID: number
  Name: string
  Mode: CampaignMode
  Status: CampaignStatus
  PostsPerDay: number
  PostingWindowStart: string
  PostingWindowEnd: string
  Timezone: string
  Persona: string
  StyleGuide: string
  JudgeThreshold: number
  JudgeMaxRetries: number
  PlatformConfigs: Record<string, PlatformConfig>
  TopicPool?: string[]
  TopicRotationState?: number
  ExpandTopics?: boolean
  TopicAnglesCount?: number
  CreatedAt: string
  UpdatedAt: string
  /** Set on an affiliate campaign; null on one that never had settings. */
  Affiliate?: AffiliateConfig | null
  /** The auto-approve switch of every mode but affiliate. */
  AutoApprove?: boolean
}

export interface GenerateNowResult {
  idea_ids: number[]
  expanded: boolean
  topic: string
  /** Drafts that went to review, drafts the ad judge turned down, platforms
   *  that failed, and angles never tried after one failed everywhere. */
  drafts: number
  judge_rejected: number
  failed: number
  skipped: number
  /** What the failed platforms said, raw. */
  detail?: string
}

export interface Draft {
  ID: number
  CampaignID: number
  /** The account it posts as; 0 = none yet (a hand-written draft). */
  TargetID?: number
  Platform: string
  BodyText: string
  /** JSON array of media ids, e.g. "[1]" — empty when nothing is attached. */
  MediaIDsJSON?: string
  /** JSON object; for reddit it carries subreddit and title. */
  PlatformMeta?: string
  /** Joined for display — the programme this post belongs to. */
  CampaignName?: string
  JudgeScore: number
  JudgeReason: string
  JudgeAttempts: number
  Status: string
  ScheduledAt: string | null
  /** A reject reason, or — for a failed draft — the platform's refusal. */
  RejectionReason?: string
  EditsCount?: number
  /** 'auto' once the Coupang auto-approve tick approved it; it stays 'auto'
   *  when a person pulls the draft back to review, and the tick skips it. */
  ApprovedBy?: 'manual' | 'auto'
  CreatedAt: string
  UpdatedAt?: string
}

export interface MediaAsset {
  ID: number
  Kind: 'image' | 'video'
  Filename: string
  StoragePath: string
  SizeBytes: number
  MIME: string
}

export interface Post {
  ID: number
  CampaignID: number
  Platform: string
  ExternalID: string
  PublishedAt: string
  EngagementJSON: string
}

// ─── Coupang (affiliate) ─────────────────────────────────────────────────────

export type CoupangSignalKind = 'price_drop' | 'high_discount' | 'new_bestseller' | 'lowest_price'

export interface AffiliateCategory {
  id: number
  coupang_cat_id: number
  name: string
  enabled: boolean
  poll_limit: number
  poll_interval_seconds: number
  last_polled_at: string | null
  /** The Threads account the category belongs to; null = both accounts share it. */
  default_target_id: number | null
  products: number
}

export interface AffiliateCatalogItem {
  id: number
  name: string
}

export interface AffiliateOverview {
  /** Whether both keys are set. The values never come to the browser. */
  keys_configured: boolean
  categories: { total: number; enabled: number }
  products: number
  signals_24h: Partial<Record<CoupangSignalKind, number>>
  /** Fresh signals no draft has used yet. */
  unclaimed_fresh: number
  stale_age_hours: number
  last_polled_at: string | null
  /** The latest poll that did something (an idle tick is left out). */
  last_poll_run: { at: string; status: string; message: string; trigger: string } | null
  /** Posts whose views jumped, the last three days, and the bar they cleared. */
  hot?: { min_views: number; hours: number; reposts?: number; items: AffiliateHotPost[] }
}

/** A Coupang post whose views reached the bar. Until `until` its campaign
 *  posts that category first. */
export interface AffiliateHotPost {
  post_id: number
  campaign: string
  product: string
  category: string
  views: number
  detected_at: string
  until: string
  /** Scheduled posts in the category that were brought forward. */
  moved: number
  url: string
}

export interface AffiliateAvatar {
  key: string
  label: string
}

export type AffiliateCategoryPatch = Partial<{
  enabled: boolean
  poll_limit: number
  poll_interval_seconds: number
  /** null clears the account. */
  default_target_id: number | null
}>

/** What a Coupang draft carries in platform_meta (kind === 'coupang'). */
export interface CoupangDraftMeta {
  kind: 'coupang'
  tone: 'promo' | 'review'
  product_id: number
  coupang_product_id: string
  product_url: string
  image_url: string
  name?: string
  brand?: string
  signal_id: number
  signal_kind: CoupangSignalKind | string
  detected_at: string
  price: number | null
  original_price: number | null
  discount_rate: number | null
  hook?: string
  hashtags?: string[]
  template?: string
  persona?: string
  attempts?: number
}

// ─── Targets ─────────────────────────────────────────────────────────────

export type TargetPlatform = 'threads' | 'reddit' | 'reddit_browser' | 'x' | 'instagram' | 'bluesky'
export type TargetStatus = 'active' | 'disabled'

/** How an account sounds. Set per account, not per campaign: a reader
 *  recognises the account, so two campaigns posting as one must not read as two
 *  people. A campaign still decides what to talk about. */
export interface Voice {
  persona: string
  style_guide: string
  banned_phrases: string[] | null
  preferred_phrases: string[] | null
}

export interface Target {
  id: number
  name: string
  platform: TargetPlatform
  status: TargetStatus
  voice: Voice
  last_check_ok?: boolean
  last_check_msg?: string
  /** Threads: when the token lapses (RFC 3339, UTC). Empty until the
   *  refresher has renewed it once. */
  token_expires_at?: string
}

export interface ThreadsCredentials {
  token: string
}

export interface RedditCredentials {
  client_id: string
  client_secret: string
  refresh_token: string
  user_agent: string
}

/** Reddit driven through browser-saas with a logged-in cookie jar — the only
 *  way to attach an image, which Reddit accepts solely at post creation. */
export interface RedditBrowserCredentials {
  browser_url: string
  api_key: string
  cookie_jar: string
  account: string
}

/** X API v2, OAuth 1.0a user context: the app's API key pair and the
 *  access token pair the developer console issues for the account. */
export interface XCredentials {
  api_key: string
  api_secret: string
  access_token: string
  access_secret: string
}

/** Our own Instagram professional account; its token is what profile lookups
 *  (Business Discovery) are made with. Never used to send. */
export interface InstagramCredentials {
  ig_user_id: string
  access_token: string
  /** RFC 3339; optional. */
  token_expires_at?: string
}

/** A Bluesky account that sends DMs by app password. */
export interface BlueskyCredentials {
  handle: string
  app_password: string
  /** Defaults to https://bsky.social. */
  pds_url?: string
}

type AnyCredentials =
  | ThreadsCredentials
  | RedditCredentials
  | RedditBrowserCredentials
  | XCredentials
  | InstagramCredentials
  | BlueskyCredentials

export interface CreateTargetPayload {
  name: string
  platform: TargetPlatform
  status?: TargetStatus
  voice?: Voice
  credentials: AnyCredentials
}

export interface UpdateTargetPayload {
  name?: string
  platform?: TargetPlatform
  status?: TargetStatus
  voice?: Voice
  credentials?: Partial<AnyCredentials>
}

// ─── Creators (influencer collaboration proposals) ───────────────────────────

export type CreatorPlatform = 'instagram' | 'threads' | 'bluesky'
export type CreatorLanguage = 'en' | 'ko' | 'ja'

export interface CreatorPost {
  text: string
  url: string
  posted_at: string
}

export interface CreatorBulkResult {
  added: number
  existing: number
  suppressed: number
  invalid: { line: string; error: string }[]
}

export interface CreatorCsvResult {
  added: number
  existing: number
  suppressed: number
  row_errors: { row: number; message: string }[]
}

export type CreatorSkipReason =
  | 'error'
  | 'followers'
  | 'off_topic'
  | 'brand'
  | 'inactive'
  | 'not_professional'
  | 'not_found'
  | 'suppressed'
  | 'existing'
  | 'no_dm'

export interface CreatorDiscoveryRun {
  id: number
  trigger: 'manual' | 'schedule'
  started_at: string
  finished_at: string | null
  status: 'running' | 'done' | 'failed'
  searched: number
  candidates: number
  looked_up: number
  added: number
  skipped: Partial<Record<CreatorSkipReason, number>>
  error: string
}

export interface CreatorDiscovery {
  enabled: boolean
  daily_lookups: number
  today_lookups: number
  search_ready: boolean
  candidates_pending: number
  running: boolean
  last_run: CreatorDiscoveryRun | null
}

// ─── Creator pools (v2: who to reach, before what to send) ───────────────────

export type PoolPlatform = 'instagram' | 'bluesky'
export type PoolStatus = 'active' | 'paused'
export type PoolSource = 'search' | 'mention' | 'seed' | 'manual' | 'csv'

/** One row of GET /api/creators/pools. */
export interface CreatorPoolItem {
  id: number
  name: string
  platform: PoolPlatform
  keywords: string[]
  followers_min: number
  /** null = no cap. */
  followers_max: number | null
  /** 0 = recent activity is not checked. */
  active_days: number
  exclude_brands: boolean
  language: CreatorLanguage
  status: PoolStatus
  discovery_enabled: boolean
  members_in: number
  last_run: CreatorDiscoveryRun | null
  updated_at: string
}

/** GET/POST/PUT /api/creators/pools/{id}: the list row plus the rest of the criteria. */
export interface CreatorPool extends CreatorPoolItem {
  seed_handles: string[]
  search_enabled: boolean
  daily_lookups: number
  timezone: string
  /** The Instagram account profiles are looked up with. */
  instagram_target_id: number | null
  created_at: string
}

/** What a create or update sends; the server merges an update onto the stored pool. */
export type CreatorPoolInput = Partial<
  Omit<CreatorPool, 'id' | 'members_in' | 'last_run' | 'updated_at' | 'created_at'>
>

/** One real account, shared by every pool. */
export interface CreatorPerson {
  id: number
  platform: PoolPlatform
  handle: string
  platform_user_id: string
  display_name: string
  profile_url: string
  bio: string
  followers: number
  media_count: number
  avg_likes: number
  avg_comments: number
  /** 0..1. */
  engagement_rate: number
  website: string
  category: string
  /** At most three evidence posts. */
  posts: CreatorPost[] | null
  dm_open: 'open' | 'closed' | 'unknown'
  looked_up_at: string | null
  /** Korean; empty when the lookup went fine. */
  lookup_error: string
}

export type MemberStatus = 'in' | 'removed'

export interface CreatorMember {
  creator: CreatorPerson
  source: PoolSource
  found_by: string
  /** 0..100. */
  score: number
  score_reason: string
  status: MemberStatus
  removed_reason: string
  added_at: string
  suppressed: boolean
  last_contacted_at: string | null
}

export type MemberSort = 'score' | 'followers' | 'engagement' | 'added'

export interface CreatorMemberFilter {
  q?: string
  status?: MemberStatus
  source?: PoolSource
  minFollowers?: number
  maxFollowers?: number
  minScore?: number
  minEngagement?: number
  sort?: MemberSort
  limit?: number
  offset?: number
}

export interface CreatorPersonDetail {
  creator: CreatorPerson
  pools: { id: number; name: string; status: MemberStatus }[]
  suppressed: boolean
  last_contacted_at: string | null
  dm_url: string
}

// ─── Creator outreaches (v2 Phase B: DM 보내기 from a pool) ──────────────────

export type OutreachStatus = 'draft' | 'active' | 'paused' | 'done'
export type OutreachAngle = 'review' | 'feature' | 'collab'
export type OutreachTone = 'polite' | 'casual'
export type RecipientState =
  | 'found'
  | 'skipped'
  | 'ready'
  | 'approved'
  | 'sent'
  | 'replied'
  | 'accepted'
  | 'declined'
  | 'do_not_contact'
export type MessageStatus = 'draft' | 'approved' | 'sending' | 'sent' | 'failed' | 'discarded'

export interface OutreachCounts {
  found: number
  ready: number
  approved: number
  sent: number
  replied: number
  accepted: number
  declined: number
  skipped: number
  do_not_contact: number
}

/** What an outreach may take from its pool. Engagement is a fraction (0.03 = 3%). */
export interface OutreachFilter {
  min_followers?: number
  max_followers?: number
  min_score?: number
  min_engagement?: number
  sources?: PoolSource[]
  /** Hand-picked; when given, only these. */
  creator_ids?: number[]
}

export type OutreachSplit =
  | { method: 'one' }
  | { method: 'followers'; boundaries: number[] }
  | { method: 'random'; groups: number; seed?: number }

export interface OutreachPreviewGroup {
  key: string
  label: string
  count: number
  sample: { id: number; handle: string; display_name: string; followers: number; score: number }[]
}

export interface OutreachPreview {
  eligible: number
  excluded: {
    removed: number
    suppressed: number
    contacted_90d: number
    in_other_outreach: number
    filtered: number
    no_dm?: number
  }
  groups: OutreachPreviewGroup[]
}

export interface OutreachGroupInput {
  key: string
  label?: string
  offer: string
  angle: OutreachAngle
  tone: OutreachTone
  template: string
  personalize: boolean
}

export interface OutreachInput {
  name: string
  pool_id: number
  filter: OutreachFilter
  split: OutreachSplit
  product_name: string
  product_url: string
  product_brief: string
  language: CreatorLanguage
  signoff: string
  daily_limit: number
  send_start_hour: number
  send_end_hour: number
  timezone: string
  bluesky_target_id?: number | null
  groups: OutreachGroupInput[]
}

export interface OutreachListItem {
  id: number
  name: string
  pool_id: number
  pool_name: string
  platform: PoolPlatform
  status: OutreachStatus
  groups: number
  recipients: number
  counts: OutreachCounts
  sent_today: number
  daily_limit: number
  updated_at: string
}

export interface Outreach {
  id: number
  name: string
  pool_id: number
  platform: PoolPlatform
  status: OutreachStatus
  product_name: string
  product_url: string
  product_brief: string
  language: CreatorLanguage
  signoff: string
  daily_limit: number
  send_start_hour: number
  send_end_hour: number
  timezone: string
  bluesky_target_id: number | null
  created_at?: string
  updated_at?: string
}

export interface OutreachGroup {
  id: number
  key: string
  label: string
  offer: string
  angle: OutreachAngle
  tone: OutreachTone
  template: string
  personalize: boolean
  counts: OutreachCounts
  clicks: number
}

export interface OutreachDetail {
  outreach: Outreach
  groups: OutreachGroup[]
  sent_today: number
  /** Hours (0-23) or "HH:MM"; open_now says whether sending is allowed this minute. */
  window: { open: number | string; close: number | string; open_now: boolean }
  next_send_at: string | null
  generating?: { running: boolean; done: number; total: number }
}

export interface OutreachGroupPatch {
  label?: string
  offer?: string
  angle?: OutreachAngle
  tone?: OutreachTone
  template?: string
  personalize?: boolean
}

export interface OutreachMessage {
  id: number
  body: string
  status: MessageStatus
  mode: 'api' | 'manual'
  error: string
  sent_at: string | null
}

export interface OutreachRecipient {
  id: number
  group_id: number
  state: RecipientState
  skip_reason: string
  creator: {
    id: number
    platform: PoolPlatform
    handle: string
    display_name: string
    profile_url: string
    followers: number
    engagement_rate: number
    bio: string
    posts: CreatorPost[] | null
  }
  score: number
  message: OutreachMessage | null
  last_contacted_at: string | null
}

export interface OutreachRecipientFilter {
  groupId?: number
  state?: RecipientState
  q?: string
  limit?: number
  offset?: number
}

export type RecipientMove = 'skip' | 'unskip' | 'replied' | 'accepted' | 'declined' | 'do_not_contact'

// ─── Product Hunt outreach ───────────────────────────────────────────────────

export type OutreachCampaignStatus = 'active' | 'paused' | 'archived'
export type OutreachProspectState =
  | 'discovered'
  | 'qualified'
  | 'connection_ready'
  | 'connection_sent'
  | 'connected'
  | 'praise_ready'
  | 'praise_sent'
  | 'replied'
  | 'cta_ready'
  | 'cta_sent'
  | 'closed'
  | 'skipped'
  | 'do_not_contact'
  | 'invalid_profile'
export type OutreachStepKind = 'connection' | 'praise' | 'cta'
export type OutreachStepStatus = 'draft' | 'approved' | 'sent' | 'discarded'
export type OutreachMessageTone = 'warm' | 'concise' | 'curious'

export interface OutreachCampaign {
  id: number
  name: string
  app_name: string
  app_tagline: string
  app_description: string
  app_url: string
  product_hunt_url: string
  product_hunt_post_id: string
  status: OutreachCampaignStatus
  timezone: string
  lookback_days: number
  max_upvotes: number
  topics: string[]
  connection_daily_limit: number
  praise_daily_limit: number
  cta_daily_limit: number
  created_at: string
  updated_at: string
}

export type CreateOutreachCampaignPayload = Omit<
  OutreachCampaign,
  'id' | 'created_at' | 'updated_at'
>

export interface OutreachProspect {
  id: number
  campaign_id: number
  source: 'product_hunt' | 'csv'
  source_maker_id: string
  source_post_id: string
  product_name: string
  product_tagline: string
  product_description: string
  product_hunt_url: string
  product_website_url: string
  product_topics: string[]
  product_upvotes: number
  product_posted_at?: string
  maker_name: string
  maker_role: string
  linkedin_url: string
  linkedin_verified_at?: string
  duplicate_confirmed_at?: string
  duplicate_confirmed_version: number
  score: number
  score_reason: string
  state: OutreachProspectState
  skip_reason: string
  last_contacted_at?: string
  created_at: string
  updated_at: string
}

export interface OutreachStep {
  id: number
  prospect_id: number
  kind: OutreachStepKind
  body_text: string
  evidence_ids: string[]
  tone: OutreachMessageTone
  status: OutreachStepStatus
  edits_count: number
  generated_at: string
  approved_at?: string
  sent_at?: string
  copy_eligible: boolean
  open_eligible: boolean
}

export interface OutreachEvent {
  id: number
  prospect_id: number
  event_type: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface OutreachImportResult {
  imported: number
  skipped: number
  prospect_ids: number[]
  row_errors: Array<{ row: number; message: string }>
  row_error_count: number
  /** Why rows were left out because they do not fit the campaign. */
  skipped_rows?: Array<{ row: number; message: string }>
}

export interface OutreachSnapshot {
  id: number
  campaign_id: number
  upvotes: number
  captured_at: string
}

export interface OutreachAnalytics {
  discovered: number
  qualified: number
  connections: number
  connected: number
  praise_sent: number
  replies: number
  cta_sent: number
  closed: number
  clicks: number
  converted_clicks: number
  connect_rate: number
  reply_rate: number
  click_rate: number
  snapshots: OutreachSnapshot[]
}

export interface OutreachProspectDetail {
  prospect: OutreachProspect
  steps: OutreachStep[]
  events: OutreachEvent[]
  contact_allowed: boolean
  safety_block_reason?: string
  duplicate_confirmation_required: boolean
}

export interface OutreachEvidenceOption {
  id: string
  label: string
  value: string
}

export interface OutreachQueueItem {
  prospect: OutreachProspect
  steps: OutreachStep[]
  evidence_options: OutreachEvidenceOption[]
  contact_allowed: boolean
  safety_block_reason?: string
  duplicate_confirmation_required: boolean
}

export interface OutreachCampaignSummary {
  campaign: OutreachCampaign
  analytics: OutreachAnalytics
}

export interface OutreachStepPreview {
  body_text: string
  evidence_id: string
  tone: OutreachMessageTone
}

export interface OutreachSnapshotSummary {
  attempted: number
  succeeded: number
  failed: number
  failures: Array<{ campaign_id: number; error: string }>
}

// ─── Redeem registry ─────────────────────────────────────────────────────────
// Codes are minted in App Store Connect / the Play console. This side reads the
// exported CSV, tracks what is left and what went out, and renders the text to
// paste. It sends nothing.

/** 'web' = sold on its own site: its codes are the checkout's discount codes,
 *  one shared code per channel, DMed to each person who asks (once each). */
export type RedeemPlatform = 'ios' | 'mac' | 'play' | 'web'
export type RedeemAppStatus = 'active' | 'paused' | 'archived'
export type RedeemCodeState = 'available' | 'issued' | 'void'

export interface RedeemApp {
  id: number
  slug: string
  name: string
  platform: RedeemPlatform
  store_id: string
  store_url: string
  /** The one-time unlock the codes were minted against. */
  product_id: string
  product_name: string
  app_price: string
  /** What a code gives: a permanent unlock, or a free period that then renews. */
  grant_kind: 'permanent' | 'trial'
  /** For a trial: how long it is free, e.g. "1 year". */
  grant_period: string
  /** For a trial: what it renews at, e.g. "$24.99/year". */
  renewal_price: string
  /** Web apps: what one code gives, as the DM says it. */
  offer?: string
  /** Code expiry, YYYY-MM-DD ('' = none). */
  expires_on: string
  /** The app itself is free until this date ('' = it is not). */
  free_until: string
  /** Frozen at 'en': the desk answers in English only. */
  lang: 'en'
  status: RedeemAppStatus
  notes: string
  /** The account this app posts and DMs as (a target). */
  target_id: number
  /** 1 = the desk runs the whole handout; 0 = requests wait for a person. */
  auto_handout: number
  /** Ceiling on codes handed out in 24h. */
  daily_cap: number
  /** Set when the giveaway was ended: no more codes, late askers are told
   *  so, and end_note goes up under each desk post. */
  ended_at?: string
  end_note: string
  created_at: string
  updated_at: string
}

export type RedeemPostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed'

/** reddit and bluesky are published and watched by the desk (reddit's
 *  comments, bluesky's DMs); the rest are posted by hand. */
export type RedeemPostPlatform = 'reddit' | 'ppomppu' | 'okky' | 'x' | 'threads' | 'instagram' | 'tiktok' | 'bluesky'
export const MANUAL_POST_PLATFORMS: RedeemPostPlatform[] = ['ppomppu', 'okky', 'x', 'threads', 'instagram', 'tiktok']
export const isManualPlatform = (p: string) => (MANUAL_POST_PLATFORMS as string[]).includes(p)

/** Bluesky's limit on a post, counted in graphemes (what a reader sees as one
 *  character), not bytes. */
export const BLUESKY_MAX_GRAPHEMES = 300

/** The desk's Bluesky login as the server shows it: the app password only
 *  as "set" / "not set". */
export interface BlueskySettings {
  handle: string
  pds_url: string
  app_password: 'set' | 'not set'
  password_set: boolean
  can_store: boolean
}

export interface BlueskySettingsUpdate {
  handle: string
  pds_url: string
  /** Empty keeps the stored one. */
  app_password?: string
  clear_password?: boolean
}

export interface BlueskyTestResult {
  ok: boolean
  did?: string
  handle?: string
  error?: string
  latency_ms?: number
}

/** Where a manual post's public codes go: in the body, or in a reply. */
export type RedeemCodeMode = 'none' | 'body' | 'reply'

/** A promo post the desk owns: written by hand, published and then watched. */
export interface RedeemPost {
  id: number
  app_id: number
  platform: RedeemPostPlatform
  /** subreddit, for reddit; free text for manual channels */
  channel: string
  title: string
  body: string
  /** Manual channels: the page to write the post on. */
  compose_url: string
  code_mode: RedeemCodeMode
  reply_text: string
  /** Desk channels: a comment (or DM) must carry this to be queued for a
   *  code. Empty takes every comment, as the older posts do. */
  claim_phrase?: string
  /** Web apps only: the shared code DMed to each person who asks under this
   *  post. Never in the body. */
  share_code?: string
  /** Reddit only: the link flair the post goes up with, by its text as the
   *  subreddit shows it. Required by some subreddits. */
  flair?: string
  /** Manual channels: body and reply with the public codes put in place. */
  rendered_body?: string
  rendered_reply?: string
  /** Live public codes allocated to this post. */
  public_codes?: number
  media_id: number
  status: RedeemPostStatus
  scheduled_at?: string
  external_id: string
  external_url: string
  /** The giveaway's closing note under this post (desk channels). */
  closed_at?: string
  close_url?: string
  close_error?: string
  error_msg: string
  published_at?: string
  created_at: string
  updated_at: string
}

/** One app's day at the handout desk. */
export interface DeskStat {
  app_id: number
  slug: string
  name: string
  issued_today: number
  issued_window: number
  requests_today: number
  replied_today: number
  pending_new: number
  failed_open: number
  available: number
  auto_handout: number
  last_issued_at?: string
}

/** One distinct failure and how often it came back. */
export interface DeskErrorGroup {
  message: string
  /** reddit | bluesky, when the message says. */
  channel?: string
  count: number
  first_at: string
  last_at: string
}

/** One desk job's recent record, from job_runs. */
export interface DeskJobHealth {
  job: string
  last_run_at?: string
  last_status?: string
  last_ok_at?: string
  last_error_at?: string
  last_error?: string
  consecutive_errors: number
  /** The read window ran out before a success: at least this many. */
  consecutive_capped?: boolean
  failing_since?: string
  errors: DeskErrorGroup[]
  unhealthy: boolean
}

export interface DeskFailedPost {
  id: number
  app_name: string
  platform: string
  channel: string
  title: string
  error_msg: string
  updated_at: string
}

/** The operator's switches on the desk. Blocklist entries are "u/name"
 *  (no code, no reply) or "r/sub" (DM still goes, no public comment). */
export interface DeskControls {
  watch_paused: boolean
  blocklist: string[]
}

/** Is the desk still reading, handing out and posting. */
export interface DeskHealth {
  /** Reddit comment reading is paused from the Requests page. */
  watch_paused?: boolean
  healthy: boolean
  checked_at: string
  jobs: DeskJobHealth[]
  failed_requests: { count: number; groups: DeskErrorGroup[] }
  stuck_requests: {
    count: number
    by_state: Record<string, number>
    oldest_at?: string
    threshold_minutes: number
  }
  failed_posts: DeskFailedPost[]
}

export interface DeskDaily {
  day: string
  slug: string
  issued: number
}

export type RedeemRequestState =
  | 'new'
  | 'issued'
  | 'sent'
  | 'replied'
  | 'duplicate'
  | 'ignored'
  | 'failed'

/** One person asking for a code. The state is the whole story of the handout. */
export interface RedeemRequest {
  id: number
  app_id: number
  post_id: number
  platform: string
  comment_id: string
  permalink: string
  requester: string
  requester_id: string
  /** Bluesky: the DM conversation the request came from (and the code went to). */
  convo_id?: string
  body: string
  state: RedeemRequestState
  code_id: number
  error_msg: string
  requested_at?: string
  handled_at?: string
}

export interface RedeemStats {
  total: number
  available: number
  issued: number
  void: number
  issued_today: number
  last_issued_at?: string
}

export interface RedeemAppWithStats {
  app: RedeemApp
  stats: RedeemStats
  /** The app's promo posts by state; booked includes one publishing now. */
  posts?: RedeemPostCounts
}

export interface RedeemPostCounts {
  draft: number
  scheduled: number
  published: number
  failed: number
  next_at?: string
}

export interface RedeemBatch {
  id: number
  app_id: number
  label: string
  expires_on: string
  note: string
  code_count: number
  available: number
  created_at: string
}

export interface RedeemCode {
  id: number
  app_id: number
  batch_id: number
  code: string
  redeem_url: string
  state: RedeemCodeState
  recipient: string
  channel: string
  note: string
  issued_at?: string
  created_at: string
  batch_label?: string
  /** The batch's expiry when it has one, else the app's. */
  expires_on?: string
}

export interface RedeemAppDetail {
  app: RedeemApp
  stats: RedeemStats
  batches: RedeemBatch[] | null
}

export interface RedeemImportResult {
  batch_id: number
  rows: number
  added: number
  /** Rows that arrived as history (already handed out) rather than as pool. */
  issued: number
  duplicates: number
  /** Lines the upload had, and the ones that gave no code. Both are absent
   *  from an import sent as a list of codes. */
  lines?: number
  skipped?: { line: number; excerpt: string; reason: string }[]
}

export interface RedeemIssued {
  code: RedeemCode
  message: string
  dm_subject: string
}

/** One code a manual post shares in public. */
export interface RedeemPublicCode {
  id: number
  code: string
  redeem_url: string
  issued_at?: string
  state: RedeemCodeState
}

// ─── The schedule feed ───────────────────────────────────────────────────────
// Desk posts and campaign drafts in one list (GET /api/schedule). Read only:
// writes go through each side's own endpoints. Every time is RFC 3339 UTC.

export type ScheduleKind = 'redeem' | 'draft'

export interface ScheduleItem {
  kind: ScheduleKind
  id: number
  /** The row's own status: a desk post's (draft…failed) or a draft's
   *  (approved, scheduled, publishing, published, failed). */
  status: string
  platform: string
  /** The subreddit for reddit; a manual channel's note; '' when the account
   *  is the channel (Threads, Bluesky). */
  channel: string
  title: string
  excerpt: string
  scheduled_at: string | null
  published_at: string | null
  /** When the Telegram heads-up for the slot went out. */
  announced_at: string | null
  external_url: string
  error_msg: string
  app: { id: number; name: string } | null
  campaign: { id: number; name: string } | null
  target: { id: number; name: string; platform: string } | null
  media: { id: number; sha256: string; mime: string } | null
  flair: string
  claim_phrase: string
  share_code: string
  judge_score: number | null
  requests: { total: number; pending: number; done: number; failed: number } | null
  /** A published draft's latest engagement, keys as polled, plus "t". */
  engagement: Record<string, number | string> | null
  /** For one stuck "publishing": when the attempt started. */
  updated_at: string
}

export interface ScheduleFeed {
  items: ScheduleItem[]
  /** Per status in the range (status filter aside), plus the waiting lane. */
  counts: Record<'scheduled' | 'publishing' | 'failed' | 'published' | 'unscheduled', number>
}

export interface ScheduleQuery {
  /** RFC 3339; omitted = open on that side. */
  from?: string
  to?: string
  source?: 'redeem' | 'campaign' | ''
  /** csv; 'unscheduled' is the waiting lane. */
  status?: string
  appId?: number
  campaignId?: number
  platform?: string
  includeUnscheduled?: boolean
}

export type CreateRedeemAppPayload = Partial<RedeemApp> & Pick<RedeemApp, 'slug' | 'name'>

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),

  // Viral — campaigns
  campaigns: () => request<{ items: Campaign[] }>('/api/campaigns'),
  campaign: (id: number) => request<Campaign>(`/api/campaigns/${id}`),
  createCampaign: (c: Partial<Campaign>) =>
    request<{ id: number }>('/api/campaigns', { method: 'POST', body: JSON.stringify(c) }),
  /** unslotted: on resume, scheduled posts whose time passed while paused
   *  lost their slot (they wait in 슬롯 없음) instead of all going out. */
  setCampaignStatus: (id: number, status: CampaignStatus) =>
    request<{ ok: boolean; unslotted?: number }>(`/api/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  /** Cadence is the campaign's business: how often, which hours, and each
   *  channel's house rules. Approving a draft reads these to pick its slot. */
  updateCampaignPacing: (id: number, pacing: CampaignPacing) =>
    request<{ ok: boolean }>(`/api/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(pacing),
    }),
  /** mode=affiliate: the whole Coupang settings object replaces the old one. */
  updateCampaignAffiliate: (id: number, affiliate: AffiliateConfig) =>
    request<{ ok: boolean }>(`/api/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ affiliate }),
    }),
  /** The plan a re-slot would apply, without applying it. */
  reslotPreview: (id: number) =>
    request<ReslotPlan>(`/api/campaigns/${id}/reslot`, {
      method: 'POST',
      body: JSON.stringify({ dry_run: true }),
    }),
  /** Applies exactly the plan the preview gave. */
  reslotApply: (id: number, plan: ReslotItem[]) =>
    request<ReslotResult>(`/api/campaigns/${id}/reslot`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),
  deleteCampaign: (id: number) =>
    request<{ ok: boolean }>(`/api/campaigns/${id}`, { method: 'DELETE' }),
  generateNow: (
    id: number,
    opts: { topic?: string; expand?: boolean; angles_count?: number } = {},
  ) =>
    request<GenerateNowResult>(`/api/campaigns/${id}/generate-now`, {
      method: 'POST',
      body: JSON.stringify(opts),
    }),

  // Viral — drafts
  createDraft: (payload: {
    campaign_id: number
    platform: string
    body_text: string
    platform_meta?: Record<string, unknown>
    target_id?: number
    source_ref?: string
    note?: string
  }) =>
    request<{ id: number; source_ref: string; status: string }>('/api/drafts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  drafts: (
    status: string = 'pending_review',
    opts: { campaignId?: number; platform?: string; subreddit?: string } = {},
  ) => {
    const qs = new URLSearchParams({ status })
    if (opts.campaignId) qs.set('campaign_id', String(opts.campaignId))
    if (opts.platform) qs.set('platform', opts.platform)
    if (opts.subreddit) qs.set('subreddit', opts.subreddit)
    return request<{ items: Draft[] }>(`/api/drafts?${qs}`)
  },
  draft: (id: number) => request<Draft>(`/api/drafts/${id}`),
  approveDraft: (id: number, scheduledAt?: string) =>
    request<{ ok: boolean }>(`/api/drafts/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(scheduledAt ? { scheduled_at: scheduledAt } : {}),
    }),
  rejectDraft: (id: number, reason: string) =>
    request<{ ok: boolean }>(`/api/drafts/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  editDraft: (
    id: number,
    bodyText: string,
    mediaIds: number[] = [],
    platformMeta: Record<string, unknown> = {},
  ) =>
    request<{ ok: boolean }>(`/api/drafts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        body_text: bodyText,
        media_ids: mediaIds,
        platform_meta: platformMeta,
      }),
    }),
  moveDraft: (id: number, campaignId: number) =>
    request<{ ok: boolean; campaign_id: number }>(`/api/drafts/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId }),
    }),
  /** A draft the judge or a person turned down, or a send stuck in publishing
   *  for over ten minutes, back to the review queue. */
  revertDraftToReview: (id: number) =>
    request<{ ok: boolean; status: string; from: string }>(`/api/drafts/${id}/revert-to-review`, {
      method: 'POST',
    }),
  /** How many drafts sit in each status, under the same filters as the list —
   *  so a view that comes up empty can say where the drafts went. */
  draftCounts: (opts: { platform?: string; campaignId?: number; subreddit?: string } = {}) => {
    const qs = new URLSearchParams()
    if (opts.campaignId) qs.set('campaign_id', String(opts.campaignId))
    if (opts.platform) qs.set('platform', opts.platform)
    if (opts.subreddit) qs.set('subreddit', opts.subreddit)
    return request<{ counts: Record<string, number>; total: number }>(`/api/drafts/counts?${qs}`)
  },
  /** Take a draft off the schedule without discarding it — it returns to
   *  review, and approving it again earns a fresh slot. */
  unscheduleDraft: (id: number) =>
    request<{ ok: boolean; status: string }>(`/api/drafts/${id}/unschedule`, {
      method: 'POST',
    }),
  /** Send a queued draft right now instead of at its slot. The request waits
   *  for the platform — an image post drives a real browser — and the error it
   *  returns is the platform's own words. */
  publishDraftNow: (id: number) =>
    request<{ ok: boolean; external_url: string }>(`/api/drafts/${id}/publish-now`, {
      method: 'POST',
    }),
  markPublishedManual: (id: number, externalUrl: string, externalId?: string) =>
    request<{ ok: boolean }>(`/api/drafts/${id}/mark-published-manual`, {
      method: 'POST',
      body: JSON.stringify({ external_url: externalUrl, external_id: externalId ?? '' }),
    }),

  // Viral — media
  media: () => request<{ items: MediaAsset[] }>('/api/media'),
  uploadMedia: async (file: File): Promise<MediaAsset> => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await fetch('/api/media', { method: 'POST', body: fd, credentials: 'include' })
    if (!res.ok) {
      const b = await res.json().catch(() => null)
      throw new ApiError(res.status, (b as { error?: string } | null)?.error ?? res.statusText)
    }
    return (await res.json()) as MediaAsset
  },
  deleteMedia: (id: number) => request<{ ok: boolean }>(`/api/media/${id}`, { method: 'DELETE' }),

  // Viral — posts
  posts: (params: { campaignId?: number; platform?: string; hours?: number } = {}) => {
    const qs = new URLSearchParams()
    if (params.campaignId) qs.set('campaign_id', String(params.campaignId))
    if (params.platform) qs.set('platform', params.platform)
    if (params.hours) qs.set('hours', String(params.hours))
    return request<{ items: Post[] }>(`/api/posts?${qs}`)
  },

  // Viral — targets
  targets: () => request<Target[]>('/api/targets'),
  createTarget: (payload: CreateTargetPayload) =>
    request<{ id: number; name: string; platform: TargetPlatform; status: TargetStatus }>(
      '/api/targets',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  updateTarget: (id: number, payload: UpdateTargetPayload) =>
    request<{ ok: boolean }>(`/api/targets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteTarget: (id: number) => request<void>(`/api/targets/${id}`, { method: 'DELETE' }),
  testTarget: (id: number) =>
    request<{ ok: boolean; msg: string; supported?: boolean }>(`/api/targets/${id}/test`, { method: 'POST' }),

  // Product Hunt outreach. Every LinkedIn action remains manual.
  outreachCampaignSummaries: (signal?: AbortSignal) =>
    request<{ items: OutreachCampaignSummary[] }>('/api/outreach/campaign-summaries', { signal }),
  outreachCampaign: (id: number, signal?: AbortSignal) =>
    request<OutreachCampaign>(`/api/outreach/campaigns/${id}`, { signal }),
  createOutreachCampaign: (payload: CreateOutreachCampaignPayload) =>
    request<OutreachCampaign>('/api/outreach/campaigns', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateOutreachCampaign: (id: number, payload: OutreachCampaign) =>
    request<OutreachCampaign>(`/api/outreach/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  discoverOutreachProspects: (campaignId: number) =>
    request<OutreachImportResult>(`/api/outreach/campaigns/${campaignId}/discover`, {
      method: 'POST',
    }),
  snapshotOutreachCampaign: (campaignId: number) =>
    request<OutreachSnapshotSummary>(`/api/outreach/campaigns/${campaignId}/snapshot`, {
      method: 'POST',
    }),
  importOutreachCSV: async (campaignId: number, file: File): Promise<OutreachImportResult> => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(`/api/outreach/campaigns/${campaignId}/import-csv`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    })
    if (res.status === 401) {
      window.location.href = loginUrlFor(location.pathname + location.search)
      throw new ApiError(401, 'unauthorized')
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new ApiError(
        res.status,
        (body as { error?: string } | null)?.error ?? res.statusText,
        body,
      )
    }
    return (await res.json()) as OutreachImportResult
  },
  outreachProspects: (campaignId: number, state?: OutreachProspectState, signal?: AbortSignal) => {
    const query = state ? `?${new URLSearchParams({ state })}` : ''
    return request<{ items: OutreachProspect[] }>(
      `/api/outreach/campaigns/${campaignId}/prospects${query}`,
      { signal },
    )
  },
  outreachQueue: (campaignId: number, signal?: AbortSignal) =>
    request<{ items: OutreachQueueItem[] }>(`/api/outreach/campaigns/${campaignId}/queue`, {
      signal,
    }),
  outreachProspect: (id: number, signal?: AbortSignal) =>
    request<OutreachProspectDetail>(`/api/outreach/prospects/${id}`, {
      signal,
    }),
  verifyOutreachLinkedIn: (id: number, url: string, confirmDuplicate = false) =>
    request<OutreachProspect>(`/api/outreach/prospects/${id}/linkedin`, {
      method: 'PUT',
      body: JSON.stringify({ url, confirm_duplicate: confirmDuplicate }),
    }),
  transitionOutreachProspect: (
    id: number,
    to: OutreachProspectState,
    reason = '',
    confirmDuplicate = false,
  ) =>
    request<OutreachProspect>(`/api/outreach/prospects/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ to, reason, confirm_duplicate: confirmDuplicate }),
    }),
  reconfirmOutreachDuplicate: (id: number) =>
    request<OutreachProspect>(`/api/outreach/prospects/${id}/reconfirm-duplicate`, {
      method: 'POST',
    }),
  generateOutreachStep: (prospectId: number, kind: OutreachStepKind) =>
    request<OutreachStep>(`/api/outreach/prospects/${prospectId}/generate/${kind}`, {
      method: 'POST',
    }),
  updateOutreachStep: (
    id: number,
    evidenceId: string,
    tone: OutreachMessageTone,
    bodyText: string,
  ) =>
    request<OutreachStep>(`/api/outreach/steps/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ evidence_id: evidenceId, tone, body_text: bodyText }),
    }),
  previewOutreachStep: (
    id: number,
    evidenceId: string,
    tone: OutreachMessageTone,
    signal?: AbortSignal,
  ) =>
    request<OutreachStepPreview>(`/api/outreach/steps/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify({ evidence_id: evidenceId, tone }),
      signal,
    }),
  approveOutreachStep: (id: number) =>
    request<OutreachStep>(`/api/outreach/steps/${id}/approve`, { method: 'POST' }),
  copyOutreachStep: (id: number) =>
    request<{ body_text: string }>(`/api/outreach/steps/${id}/copy`, { method: 'POST' }),
  outreachAnalytics: (campaignId: number, signal?: AbortSignal) =>
    request<OutreachAnalytics>(`/api/outreach/campaigns/${campaignId}/analytics`, { signal }),

  // Redeem registry
  redeemApps: (signal?: AbortSignal) =>
    request<{ items: RedeemAppWithStats[] }>('/api/redeem/apps', { signal }),
  redeemApp: (id: number, signal?: AbortSignal) =>
    request<RedeemAppDetail>(`/api/redeem/apps/${id}`, { signal }),
  addSharedCode: (
    id: number,
    payload: { code: string; uses: number; url?: string; expires_on?: string },
  ) =>
    request<{ batch_id: number; added: number }>(`/api/redeem/apps/${id}/shared-codes`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  createRedeemApp: (payload: CreateRedeemAppPayload) =>
    request<RedeemApp>('/api/redeem/apps', { method: 'POST', body: JSON.stringify(payload) }),
  /** `unschedule` takes the row's scheduled posts off the schedule in the
   *  same request; the reply says how many. */
  endRedeemGiveaway: (id: number, endNote: string, unschedule = false) =>
    request<RedeemAppDetail & { unscheduled?: number }>(`/api/redeem/apps/${id}/end`, {
      method: 'POST',
      body: JSON.stringify({ end_note: endNote, unschedule }),
    }),
  reopenRedeemGiveaway: (id: number) =>
    request<RedeemAppDetail & { unslotted?: number }>(`/api/redeem/apps/${id}/reopen`, { method: 'POST' }),
  retryRedeemPostClose: (id: number) =>
    request<{ ok: boolean }>(`/api/redeem/posts/${id}/close-retry`, { method: 'POST' }),
  updateRedeemApp: (id: number, payload: RedeemApp) =>
    request<RedeemApp & { unslotted?: number }>(`/api/redeem/apps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  /** Retire a whole batch, or put it back. Issued codes are left alone. */
  setRedeemBatchState: (appId: number, batchId: number, state: 'void' | 'available') =>
    request<{ moved: number; state: string; left_void?: number }>(
      `/api/redeem/apps/${appId}/batches/${batchId}/state`,
      { method: 'POST', body: JSON.stringify({ state }) },
    ),
  importRedeemCodes: (
    id: number,
    payload: { label?: string; expires_on?: string; note?: string; csv: string },
  ) =>
    request<RedeemImportResult>(`/api/redeem/apps/${id}/import`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  importRedeemCSVFile: async (
    id: number,
    file: File,
    meta: { label?: string; expires_on?: string; note?: string } = {},
  ): Promise<RedeemImportResult> => {
    const form = new FormData()
    form.append('file', file)
    if (meta.label) form.append('label', meta.label)
    if (meta.expires_on) form.append('expires_on', meta.expires_on)
    if (meta.note) form.append('note', meta.note)
    const res = await fetch(`/api/redeem/apps/${id}/import`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    })
    if (res.status === 401) {
      window.location.href = loginUrlFor(location.pathname + location.search)
      throw new ApiError(401, 'unauthorized')
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new ApiError(
        res.status,
        (body as { error?: string } | null)?.error ?? res.statusText,
        body,
      )
    }
    return (await res.json()) as RedeemImportResult
  },
  // ── the desk: promo posts and the request queue ───────────────────────
  /** What a handout would say, with a placeholder where the code goes — the
   *  wording rotates, so a few samples show the range. No code is spent. */
  redeemMessagePreview: (appId: number, count = 3) =>
    request<{ items: { subject: string; body: string }[]; placeholder: string; review_url: string }>(
      `/api/redeem/apps/${appId}/message-preview?count=${count}`,
    ),
  /** Drops the record. A published post stays up on the platform — this only
   *  forgets it here — and the requests it attracted keep their history. */
  deleteRedeemPost: (id: number) =>
    request<void>(`/api/redeem/posts/${id}`, { method: 'DELETE' }),
  /** Counts cover every status under the same app filter, so a narrowed view
   *  can still say what else is waiting. */
  redeemPosts: (appId?: number, status = '') => {
    const qs = new URLSearchParams()
    if (appId) qs.set('app_id', String(appId))
    if (status && status !== 'all') qs.set('status', status)
    return request<{ items: RedeemPost[]; counts: Record<string, number> }>(
      `/api/redeem/posts?${qs}`,
    )
  },
  /** One post as the list shows it — for a detail opened by its address. */
  redeemPost: (id: number) => request<RedeemPost>(`/api/redeem/posts/${id}`),
  createRedeemPost: (payload: Partial<RedeemPost>) =>
    request<RedeemPost>('/api/redeem/posts', { method: 'POST', body: JSON.stringify(payload) }),
  updateRedeemPost: (id: number, payload: Partial<RedeemPost>) =>
    request<RedeemPost>(`/api/redeem/posts/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  /** A null time takes the post off the queue without discarding it. */
  scheduleRedeemPost: (id: number, scheduledAt: string | null) =>
    request<{ ok: boolean }>(`/api/redeem/posts/${id}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduled_at: scheduledAt }),
    }),
  /** A manual post's public codes, numbered in the order people comment against. */
  redeemPublicCodes: (postId: number) =>
    request<{ items: RedeemPublicCode[] }>(`/api/redeem/posts/${postId}/public-codes`),
  /** Makes the post hold `count` live codes — only the difference is issued. */
  topUpRedeemPublicCodes: (postId: number, count: number) =>
    request<{ issued: number; post: RedeemPost }>(`/api/redeem/posts/${postId}/public-codes`, {
      method: 'POST',
      body: JSON.stringify({ count }),
    }),
  /** The operator put a manual post up by hand; this records where. */
  markRedeemPostPublished: (postId: number, externalUrl: string) =>
    request<RedeemPost>(`/api/redeem/posts/${postId}/mark-published`, {
      method: 'POST',
      body: JSON.stringify({ external_url: externalUrl }),
    }),
  /** Waits for the platform: the answer wanted is whether it went out. */
  publishRedeemPostNow: (id: number) =>
    request<{ ok: boolean; external_url: string }>(`/api/redeem/posts/${id}/publish-now`, {
      method: 'POST',
    }),
  /** The desk's Bluesky account (Settings → Bluesky). */
  blueskySettings: () => request<BlueskySettings>('/api/redeem/bluesky'),
  saveBlueskySettings: (payload: BlueskySettingsUpdate) =>
    request<BlueskySettings>('/api/redeem/bluesky', { method: 'PUT', body: JSON.stringify(payload) }),
  /** Logs in with what is saved and reports the account. */
  testBluesky: () => request<BlueskyTestResult>('/api/redeem/bluesky/test', { method: 'POST' }),
  redeemControls: () => request<DeskControls>('/api/redeem/controls'),
  saveRedeemControls: (c: DeskControls) =>
    request<DeskControls>('/api/redeem/controls', { method: 'PUT', body: JSON.stringify(c) }),
  /** Today per app, plus a few days of history for the trend. */
  redeemHealth: () => request<DeskHealth>('/api/redeem/health'),
  redeemStats: (days = 7) =>
    request<{ items: DeskStat[]; daily: DeskDaily[]; window_days: number }>(
      `/api/redeem/stats?days=${days}`,
    ),
  redeemRequests: (state = '', appId = 0, limit = 100) => {
    const qs = new URLSearchParams({ limit: String(limit) })
    if (state && state !== 'all') qs.set('state', state)
    if (appId) qs.set('app_id', String(appId))
    return request<{ items: RedeemRequest[]; counts: Record<string, number> }>(
      `/api/redeem/requests?${qs}`,
    )
  },
  /** Send the code this request already holds, again — for a delivery that
   *  failed without saying so. Spends no new code. */
  resendRedeemRequest: (id: number) =>
    request<RedeemRequest>(`/api/redeem/requests/${id}/resend`, { method: 'POST' }),
  /** Issue, send and reply — the same path the cron takes when automatic. */
  handleRedeemRequest: (id: number) =>
    request<RedeemRequest>(`/api/redeem/requests/${id}/handle`, { method: 'POST' }),
  // 'sent' on a failed row = the DM did arrive; the server posts the public
  // receipt and answers with the row's state after it (reply_error if not).
  setRedeemRequestState: (id: number, state: RedeemRequestState) =>
    request<{ ok: boolean; state: string; reply_error?: string }>(`/api/redeem/requests/${id}/state`, {
      method: 'POST',
      body: JSON.stringify({ state }),
    }),
  /** Read the inbox now instead of waiting for the three-minute tick. */
  redeemWatchNow: () =>
    request<{ ok: boolean; counts: Record<string, number> }>('/api/redeem/watch-now', {
      method: 'POST',
    }),
  redeemCodes: (
    id: number,
    params: { state?: RedeemCodeState | ''; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ) => {
    const qs = new URLSearchParams()
    if (params.state) qs.set('state', params.state)
    if (params.limit) qs.set('limit', String(params.limit))
    if (params.offset) qs.set('offset', String(params.offset))
    return request<{ items: RedeemCode[]; total: number }>(`/api/redeem/apps/${id}/codes?${qs}`, {
      signal,
    })
  },
  issueRedeemCodes: (
    id: number,
    payload: {
      recipient: string
      count?: number
      channel?: string
      note?: string
      code?: string
      /** Set only after the matching warning was shown and accepted. */
      override_cap?: boolean
      override_repeat?: boolean
    },
  ) =>
    request<{ items: RedeemIssued[] }>(`/api/redeem/apps/${id}/issue`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Both programmes' posts in one timeline. */
  schedule: (q: ScheduleQuery = {}) => {
    const qs = new URLSearchParams()
    if (q.from) qs.set('from', q.from)
    if (q.to) qs.set('to', q.to)
    if (q.source) qs.set('source', q.source)
    if (q.status) qs.set('status', q.status)
    if (q.appId) qs.set('app_id', String(q.appId))
    if (q.campaignId) qs.set('campaign_id', String(q.campaignId))
    if (q.platform) qs.set('platform', q.platform)
    if (q.includeUnscheduled) qs.set('include_unscheduled', '1')
    return request<ScheduleFeed>(`/api/schedule?${qs}`)
  },
  setRedeemCodeState: (codeId: number, state: 'available' | 'void') =>
    request<{ ok: boolean; state: string }>(`/api/redeem/codes/${codeId}/state`, {
      method: 'POST',
      body: JSON.stringify({ state }),
    }),
  affiliateOverview: (signal?: AbortSignal) => request<AffiliateOverview>('/api/affiliate/overview', { signal }),
  /** When the Coupang auto-approve tick last ran; null when it never has. */
  autoApproveLastRun: async () => {
    const r = await request<JobRunsResponse>('/api/admin/jobs/runs?job=affiliate_auto_approve&limit=1')
    return r.runs?.[0]?.started_at ?? null
  },
  affiliateCategories: (signal?: AbortSignal) =>
    request<{ items: AffiliateCategory[]; catalog: AffiliateCatalogItem[] }>('/api/affiliate/categories', { signal }),
  addAffiliateCategory: (coupangCatId: number) =>
    request<AffiliateCategory>('/api/affiliate/categories', {
      method: 'POST',
      body: JSON.stringify({ coupang_cat_id: coupangCatId }),
    }),
  updateAffiliateCategory: (id: number, patch: AffiliateCategoryPatch) =>
    request<AffiliateCategory>(`/api/affiliate/categories/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteAffiliateCategory: (id: number) =>
    request<{ ok: boolean }>(`/api/affiliate/categories/${id}`, { method: 'DELETE' }),
  /** Collects one category now. 409 when no keys are set. */
  pollAffiliateCategory: (id: number) =>
    request<{ category: AffiliateCategory; changed: number }>(`/api/affiliate/categories/${id}/poll`, { method: 'POST' }),
  affiliateAvatars: (signal?: AbortSignal) => request<{ items: AffiliateAvatar[] }>('/api/affiliate/avatars', { signal }),
  // Creator pools
  creatorPools: (signal?: AbortSignal) => request<{ items: CreatorPoolItem[] }>('/api/creators/pools', { signal }),
  creatorPool: (id: number, signal?: AbortSignal) => request<CreatorPool>(`/api/creators/pools/${id}`, { signal }),
  createCreatorPool: (payload: CreatorPoolInput) =>
    request<CreatorPool>('/api/creators/pools', { method: 'POST', body: JSON.stringify(payload) }),
  updateCreatorPool: (id: number, payload: CreatorPoolInput) =>
    request<CreatorPool>(`/api/creators/pools/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  /** Removes the pool and its memberships, never the people. */
  deleteCreatorPool: (id: number) => request<void>(`/api/creators/pools/${id}`, { method: 'DELETE' }),
  /** 202 {run_id}; 409 with a Korean reason when a run is going, an Instagram pool has no lookup account, or the day's lookups are used. */
  discoverPool: (id: number, maxLookups?: number) =>
    request<{ run_id: number }>(`/api/creators/pools/${id}/discover`, {
      method: 'POST',
      body: JSON.stringify(maxLookups ? { max_lookups: maxLookups } : {}),
    }),
  poolDiscovery: (id: number, signal?: AbortSignal) =>
    request<CreatorDiscovery>(`/api/creators/pools/${id}/discovery`, { signal }),
  poolMembers: (id: number, f: CreatorMemberFilter = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams()
    if (f.q) qs.set('q', f.q)
    if (f.status) qs.set('status', f.status)
    if (f.source) qs.set('source', f.source)
    if (f.minFollowers) qs.set('min_followers', String(f.minFollowers))
    if (f.maxFollowers) qs.set('max_followers', String(f.maxFollowers))
    if (f.minScore) qs.set('min_score', String(f.minScore))
    if (f.minEngagement) qs.set('min_engagement', String(f.minEngagement))
    if (f.sort) qs.set('sort', f.sort)
    if (f.limit) qs.set('limit', String(f.limit))
    if (f.offset) qs.set('offset', String(f.offset))
    return request<{ items: CreatorMember[]; total: number }>(`/api/creators/pools/${id}/members?${qs}`, { signal })
  },
  /** One handle or profile URL per line; a bare name takes the pool's platform. */
  bulkAddPoolMembers: (id: number, lines: string) =>
    request<CreatorBulkResult>(`/api/creators/pools/${id}/members/bulk`, { method: 'POST', body: JSON.stringify({ lines }) }),
  /** A CSV with a header row and a `handle` column. */
  importPoolCsv: (id: number, csv: string) =>
    request<CreatorCsvResult>(`/api/creators/pools/${id}/members/import-csv`, {
      method: 'POST',
      body: csv,
      headers: { 'Content-Type': 'text/csv' },
    }),
  removePoolMember: (poolId: number, creatorId: number, reason = '') =>
    request<unknown>(`/api/creators/pools/${poolId}/members/${creatorId}/remove`, { method: 'POST', body: JSON.stringify({ reason }) }),
  restorePoolMember: (poolId: number, creatorId: number) =>
    request<unknown>(`/api/creators/pools/${poolId}/members/${creatorId}/restore`, { method: 'POST' }),
  creatorPerson: (id: number, signal?: AbortSignal) => request<CreatorPersonDetail>(`/api/creators/people/${id}`, { signal }),
  enrichCreatorPerson: (id: number) => request<unknown>(`/api/creators/people/${id}/enrich`, { method: 'POST' }),
  /** Global: the person is also taken out of every pool. */
  creatorDoNotContact: (id: number, reason = '') =>
    request<unknown>(`/api/creators/people/${id}/do-not-contact`, { method: 'POST', body: JSON.stringify({ reason }) }),

  // Creator outreaches
  creatorOutreaches: (signal?: AbortSignal) => request<{ items: OutreachListItem[] }>('/api/creators/outreaches', { signal }),
  /** Writes nothing; counts who would be taken and how they would split. */
  previewOutreach: (payload: { pool_id: number; filter: OutreachFilter; split: OutreachSplit }, signal?: AbortSignal) =>
    request<OutreachPreview>('/api/creators/outreaches/preview', { method: 'POST', body: JSON.stringify(payload), signal }),
  /** 201 detail; 422 when nobody is eligible or a template has no/two {{link}}; 409 on a duplicate name. */
  createOutreach: (payload: OutreachInput) =>
    request<OutreachDetail>('/api/creators/outreaches', { method: 'POST', body: JSON.stringify(payload) }),
  creatorOutreach: (id: number, signal?: AbortSignal) => request<OutreachDetail>(`/api/creators/outreaches/${id}`, { signal }),
  updateOutreach: (id: number, payload: Partial<Omit<Outreach, 'id' | 'pool_id' | 'platform'>>) =>
    request<OutreachDetail>(`/api/creators/outreaches/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteOutreach: (id: number) => request<unknown>(`/api/creators/outreaches/${id}`, { method: 'DELETE' }),
  updateOutreachGroup: (id: number, groupId: number, payload: OutreachGroupPatch) =>
    request<OutreachGroup>(`/api/creators/outreaches/${id}/groups/${groupId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  /** Up to three previews from the group's people; nothing is saved. */
  sampleOutreachGroup: (id: number, groupId: number) =>
    request<{ messages: { creator: { id: number; handle: string; display_name: string }; body: string }[] }>(
      `/api/creators/outreaches/${id}/groups/${groupId}/sample`,
      { method: 'POST' },
    ),
  /** 202 {started}; 409 while a generation is running. rewrite also redoes drafts waiting for approval. */
  generateOutreach: (id: number, payload: { group_id?: number; rewrite?: boolean } = {}) =>
    request<{ started: number }>(`/api/creators/outreaches/${id}/generate`, { method: 'POST', body: JSON.stringify(payload) }),
  outreachRecipients: (id: number, f: OutreachRecipientFilter = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams()
    if (f.groupId) qs.set('group_id', String(f.groupId))
    if (f.state) qs.set('state', f.state)
    if (f.q) qs.set('q', f.q)
    qs.set('limit', String(f.limit ?? 100))
    if (f.offset) qs.set('offset', String(f.offset))
    return request<{ items: OutreachRecipient[]; total: number }>(`/api/creators/outreaches/${id}/recipients?${qs}`, { signal })
  },
  moveRecipient: (id: number, groupId: number) =>
    request<unknown>(`/api/creators/recipients/${id}/move`, { method: 'POST', body: JSON.stringify({ group_id: groupId }) }),
  transitionRecipient: (id: number, to: RecipientMove, reason = '') =>
    request<unknown>(`/api/creators/recipients/${id}/transition`, { method: 'POST', body: JSON.stringify({ to, reason }) }),
  saveRecipientMessage: (id: number, body: string) =>
    request<unknown>(`/api/creators/recipients/${id}/message`, { method: 'PUT', body: JSON.stringify({ body }) }),
  approveRecipientMessage: (id: number) => request<unknown>(`/api/creators/recipients/${id}/message/approve`, { method: 'POST' }),
  discardRecipientMessage: (id: number) => request<unknown>(`/api/creators/recipients/${id}/message/discard`, { method: 'POST' }),
  regenerateRecipientMessage: (id: number) => request<unknown>(`/api/creators/recipients/${id}/message/regenerate`, { method: 'POST' }),
  markRecipientSent: (id: number) => request<unknown>(`/api/creators/recipients/${id}/message/mark-sent`, { method: 'POST' }),
  /** Approved manual messages only; the first call makes the tracked link. */
  copyRecipientMessage: (id: number) =>
    request<{ body_text: string; dm_url: string }>(`/api/creators/recipients/${id}/message/copy`),
}
